import React from 'react';

/**
 * Generate print content as a string
 * @param {Object} params - Parameters for print content
 * @param {React.Node} params.artifactContent - HTML content to print
 * @param {string} params.documentTitle - Title of the document
 * @returns {string} HTML string ready for print window
 */
const generatePrintContent = ({ artifactContent, documentTitle }) => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${documentTitle || "Führungsdokument"}</title>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          /* Reset for printing */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          html, body {
            width: 100%;
            font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Helvetica, Arial, sans-serif;
            line-height: 1.4;
            color: #000;
            background: white;
          }

          /* Main container */
          .print-container {
            width: 100%;
            padding: 0.3in;
            margin: 0 auto;
          }

          /* Clean document header - Title page */
          .apple-doc-header {
            height: calc(100vh - 1in); /* Take up approximately one page */
            min-height: 9in; /* Minimum height for US letter/A4 page */
            margin-bottom: 0;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e5e5;
            display: flex;
            flex-direction: column;
            page-break-after: always; /* Force page break after header */
          }

          .apple-doc-title {
            font-size: 18pt;
            font-weight: 700;
            color: #000000;
            letter-spacing: -0.022em;
            margin-bottom: 0.15in;
            line-height: 1.2;
          }

          .apple-doc-subtitle {
            font-size: 13pt;
            font-weight: 500;
            color: #444444;
            letter-spacing: -0.011em;
            margin-bottom: 0.1in;
          }

          .apple-doc-conversation-name {
            font-size: 15pt;
            font-weight: 500;
            color: #004080;
            margin-top: 0.1in;
            margin-bottom: 0.15in;
            letter-spacing: -0.01em;
            line-height: 1.3;
            border-left: 3px solid #0066cc;
            padding-left: 10px;
          }

          .apple-doc-date {
            font-size: 9pt;
            color: #444;
            margin-top: auto; /* Push to bottom of flex container */
            margin-bottom: 0.1in;
            font-weight: normal;
            text-align: right;
          }

          /* Ensure content sections start on next page */
          .apple-doc-section:first-of-type {
            page-break-before: always;
            margin-top: 0;
          }

          /* Improved section styling */
          .apple-doc-section {
            margin-bottom: 0.4in;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .apple-doc-section-title {
            font-size: 14pt;
            font-weight: 600;
            color: #000000;
            margin-top: 0.25in;
            margin-bottom: 0.15in;
            letter-spacing: -0.011em;
            text-transform: capitalize;
          }

          /* Enhanced subsection styling */
          .apple-doc-subsection {
            margin-left: 0;
            margin-bottom: 0.2in;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .apple-doc-subsection-title {
            font-size: 12pt;
            font-weight: 600;
            color: #000000;
            margin-bottom: 0.1in;
          }

          /* Text styling */
          .apple-doc-text {
            font-size: 11pt;
            line-height: 1.4;
            color: #333333;
            margin-bottom: 0.1in;
          }

          /* List styling */
          .list-disc {
            list-style-type: disc;
            margin-left: 0.2in;
            margin-bottom: 0.2in;
          }

          .list-disc li {
            margin-bottom: 0.05in;
            line-height: 1.4;
            font-size: 11pt;
          }

          /* Options styling */
          .option-details {
            margin-top: 0.1in;
            margin-bottom: 0.2in;
          }

          .detail-heading {
            font-weight: 600;
            font-size: 11pt;
            color: #000000;
            margin-bottom: 0.05in;
          }

          /* Footer styling */
          .apple-doc-footer {
            font-size: 10pt;
            color: #666666;
            margin-top: 0.3in;
            padding-top: 0.1in;
            border-top: 1px solid #e5e5e5;
            text-align: left;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Print settings */
          @page {
            margin: 0.3in;
            size: auto;
          }

          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          ${artifactContent}
        </div>
        <script>
          // Auto-print when content is loaded
          window.onload = function() {
            setTimeout(function() {
              window.print();
              // Optional: close window after printing
              // window.close();
            }, 500);
          }
        </script>
      </body>
    </html>
  `;
};

/**
 * Print the artifact content in a new window
 * @param {Object} params - Print parameters
 * @param {string} params.documentTitle - Title of the document
 * @returns {Function} Function to handle print action
 */
const printArtifact = ({ documentTitle }) => {
  return () => {
    // Get a clone of the artifact content to avoid modifying the original
    const artifactElement = document.querySelector('.apple-doc');

    if (!artifactElement) {
      console.error('Artifact element not found');
      alert('Fehler: Dokument-Element konnte nicht gefunden werden.');
      return;
    }

    const artifactContent = artifactElement.cloneNode(true);

    // Create a new window specifically for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (!printWindow) {
      alert("Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite, um zu drucken.");
      return;
    }

    // Create HTML content with proper print styles
    const printContent = generatePrintContent({
      artifactContent: artifactContent.outerHTML,
      documentTitle
    });

    // Write content to print window
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };
};

export default printArtifact;
